// src/lib/layouts/deriveClipPosition.ts
import { computeFillerRegion } from './fillerPlaylist';

export interface FractionalRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Where the filler strip sits relative to the clip cell along the
 *  composition's split axis. 'before' = above (portrait) / left (landscape);
 *  'after' = below / right; 'cross' = the strip runs on the other axis (or is
 *  degenerate — e.g. a full-canvas cell leaves no filler). */
export type FillerSide = 'before' | 'after' | 'cross';

const EPS = 1e-9;

export function isValidFractionalRect(
  rect: FractionalRect | undefined | null,
): rect is FractionalRect {
  return (
    !!rect &&
    Number.isFinite(rect.x) &&
    Number.isFinite(rect.y) &&
    Number.isFinite(rect.w) &&
    Number.isFinite(rect.h) &&
    rect.w > 0 &&
    rect.h > 0
  );
}

export function classifyFillerSide(
  clipRect: FractionalRect,
  isLandscape: boolean,
): FillerSide {
  const strip = computeFillerRegion(clipRect, isLandscape);
  if (!isLandscape) {
    // Vertical bands (above/below) span the full width; side strips span the
    // full height. A degenerate strip (zero area) classifies as cross.
    if (strip.w >= 1 - EPS && strip.h > EPS) {
      return strip.y + strip.h <= clipRect.y + EPS ? 'before' : 'after';
    }
    return 'cross';
  }
  if (strip.h >= 1 - EPS && strip.w > EPS) {
    return strip.x + strip.w <= clipRect.x + EPS ? 'before' : 'after';
  }
  return 'cross';
}

/** The legacy `clipPosition` implied by a free-floating cell: filler
 *  below/right → the clip comes first ('top'); filler above/left → 'bottom'.
 *  With the strip on the cross axis, the cell's center along the split axis
 *  decides. Junk rects fall back to 'top' (the historical default). */
export function deriveClipPosition(
  clipRect: FractionalRect | undefined | null,
  isLandscape: boolean,
): 'top' | 'bottom' {
  if (!isValidFractionalRect(clipRect)) return 'top';
  const side = classifyFillerSide(clipRect, isLandscape);
  if (side === 'before') return 'bottom';
  if (side === 'after') return 'top';
  const center = isLandscape
    ? clipRect.x + clipRect.w / 2
    : clipRect.y + clipRect.h / 2;
  return center <= 0.5 ? 'top' : 'bottom';
}
